import { Controller } from "@hotwired/stimulus";
import CreatableSelect from "react-select/creatable";
import { createRoot } from "react-dom/client";
import { useState, useEffect } from "react";

const reactSelectStyles = {
  control: (state) =>
    `block w-full rounded-lg bg-white px-3.5 text-base !text-gray-900 !cursor-pointer
           placeholder:!text-gray-400 outline-none ring-1 !shadow-none !border-0 py-1
           ${state.isFocused ? "ring-2 !ring-default-600" : "!ring-gray-300"}
           ${state.isError ? "!ring-red-500" : ""}`,
  placeholder: () => "text-base !text-gray-400",
  input: () =>
    "text-base !text-gray-900 focus:!shadow-none focus:!ring-0 [&>input]:!ring-0",
  inputContainer: () =>
    "!shadow-none !ring-0 !outline-none [&>input]:focus:!shadow-none [&>input]:focus:!ring-0",
  singleValue: () => "text-base !text-gray-900",
  valueContainer: () => "!p-0",
  multiValue: () =>
    "!rounded bg-white !bg-default-50 border !border-default-200 !text-default-600 overflow-hidden flex items-center text-base font-medium px-1",
  multiValueLabel: () => "text-base font-medium !text-default-600 ",
  multiValueRemove: (state) =>
    `p-0 rounded-full h-5 w-5 flex items-center justify-center mr-1 border-0
           ${state.isFocused ? "!bg-default-600 text-white" : "!bg-default-50 !text-default-600"}`,
  menu: () => "mt-1 overflow-hidden bg-white rounded-md shadow-lg",
  menuList: () => "text-base !text-gray-900 max-h-60",
  option: (state) =>
    `px-3.5 py-2 !cursor-pointer
           ${state.isSelected ? "!bg-default-50 !text-default-600" : ""}
           ${state.isFocused ? "!bg-default-50 !text-default-600" : ""}`,
  indicatorsContainer: () =>
    "!bg-default-50 [&_svg]:!text-default-600 !cursor-pointer rounded-full !h-[30px]",
  indicatorSeparator: () => "!bg-transparent",
};

function Component({
  value = [],
  options = [],
  name,
  multiple = true,
}: {
  value: string[];
  options?: string[];
  name: string;
  multiple: boolean;
}) {
  const initialValues = value.map((v) => ({
    label: v.trim(),
    value: v.trim(),
  }));

  const [inputValues, setInputValues] = useState(initialValues);

  const renderedOptions = options.map((opt) => ({
    label: opt.trim(),
    value: opt.trim(),
  }));

  useEffect(() => {
    if (!document.getElementById("select-portal")) {
      const portalContainer = document.createElement("div");
      portalContainer.id = "select-portal";
      document.body.appendChild(portalContainer);
    }
  }, []);

  return (
    <>
      <CreatableSelect
        isMulti={multiple}
        defaultValue={initialValues}
        placeholder="Sélectionner..."
        onChange={setInputValues}
        options={renderedOptions}
        menuPortalTarget={document.body} // Render menu in portal
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
          }),
        }}
        classNames={reactSelectStyles}
      />
      {multiple &&
        inputValues.map((option) => (
          <input
            type="hidden"
            name={`${name}[]`}
            value={option.value}
            key={option.value}
          />
        ))}

      {!multiple && (
        <input
          type="hidden"
          name={`${name}[]`}
          value={inputValues.value}
          key={inputValues.value}
        />
      )}
    </>
  );
}

export default class extends Controller {
  static targets = ["input", "select"];
  root: any;

  connect() {
    const options = JSON.parse(this.element.dataset.options || "[]");
    const value = JSON.parse(this.element.dataset.value || "[]");
    const multiple = this.element.dataset.multiple === "false" ? false : true;
    this.root = createRoot(this.selectTarget);
    this.root.render(
      <Component
        name={this.element.dataset.name}
        options={options || []}
        value={value}
        multiple={multiple}
      />,
    );
  }

  disconnect() {
    if (this.root) {
      this.root.unmount();
    }
  }
}
